/**
 * `EndpointColumn` — the right column (desktop): per-phase call cards.
 * ADR-0163 §2: a thin container that maps `phases.slice(0, step + 1)`
 * and renders `HumanCallCard` or `CodeCallCard` per row based on the
 * global view mode. The `Human | Code` toggle lives in this column's
 * header on md+ (one toggle locus per viewport). The internal scroll
 * cage is gone — the page scrolls (ADR-0163 §6).
 */

import { Server } from 'lucide-react';

import { Card } from '../../../components/atoms/index.js';
import { useC } from '../../../lib/theme.js';
import type { Phase } from '../journeys/types.js';
import type { Fact } from '../lib/facts.js';
import type { ViewMode } from '../hooks/useViewMode.js';
import { collapseSkipped } from './AgentColumn.js';
import { CodeCallCard } from './CodeCallCard.js';
import { HumanCallCard } from './HumanCallCard.js';
import { ResetIconButton } from './ResetIconButton.js';
import { ViewToggle } from './ViewToggle.js';

const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";

export interface EndpointColumnProps {
  phases: Phase[];
  step: number;
  mode: ViewMode;
  onChangeMode: (mode: ViewMode) => void;
  factsFor: (phase: Phase) => readonly Fact[];
  canReset: boolean;
  onReset: () => void;
}

export function EndpointColumn({ phases, step, mode, onChangeMode, factsFor, canReset, onReset }: EndpointColumnProps): JSX.Element {
  const C = useC();
  return (
    <Card className="p-5">
      <div className="flex items-center gap-3 mb-4 pb-4 flex-wrap" style={{ borderBottom: `1px solid ${C.border}` }}>
        <div className="flex items-center justify-center" style={{ width: 32, height: 32, background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 2 }}>
          <Server size={16} color={C.limeFg} />
        </div>
        <div className="flex-1 min-w-0">
          <span style={{ fontFamily: sansFont, fontSize: 14, color: C.text, fontWeight: 500 }}>Sill edge</span>
          <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, marginTop: 2 }}>edge.sill.so · live discovery + illustration</div>
        </div>
        <ResetIconButton canReset={canReset} onReset={onReset} />
        <ViewToggle mode={mode} onChange={onChangeMode} />
      </div>

      <div className="space-y-2">
        {collapseSkipped(phases.slice(0, step + 1)).map((p) => {
          if (p.skipped) {
            return (
              <div key={p.id} className="p-3" style={{ background: C.surface2, border: `1px dashed ${C.border}`, borderRadius: 2, opacity: 0.6 }}>
                <div style={{ fontFamily: monoFont, fontSize: 11, color: C.dim }}>(no request — skipped)</div>
              </div>
            );
          }
          // `hideOutcome` — on desktop the narration renders in the
          // adjacent AgentColumn at the same row; repeating it here
          // would make the merchant read the story twice (and breaks
          // the single-match posture of the pinned narration tests).
          return mode === 'human' ? (
            <HumanCallCard key={p.id} phase={p} facts={factsFor(p)} hideOutcome />
          ) : (
            <CodeCallCard key={p.id} phase={p} />
          );
        })}
        {step < 0 && (
          <div className="text-center py-12" style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>
            Requests appear here when you press Run.
          </div>
        )}
      </div>
    </Card>
  );
}
